import { useEffect, useRef, useState } from "react";
import { Scanner, type IDetectedBarcode } from "@yudiel/react-qr-scanner";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

interface ButtonScanQRProps {
  onScan: (value: string | null) => void;
  error: string | null;
  onError: (value: string | null) => void;
  label?: string;
  title?: string;
  description?: string;
  autoClose?: boolean;
}

const ButtonScanQR = ({
  label = " Comenzar inspección",
  title = "",
  description = "",
  error,
  onScan,
  onError,
  autoClose = false,
}: ButtonScanQRProps) => {
  const [openScan, setOpenScan] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);
  const [hasCameraAccess, setHasCameraAccess] = useState<boolean | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">(
    "environment"
  );
  const streamRef = useRef<MediaStream | null>(null);

  const handleScan = (detectedCodes: IDetectedBarcode[]) => {
    if (detectedCodes?.length > 0) {
      const firstCode = detectedCodes[0].rawValue;
      if (firstCode) {
        setScanResult(firstCode);
        onScan(firstCode);
        if (autoClose) {
          setTimeout(() => {
            setOpenScan(false);
            resetScan();
          }, 1000);
        }
      }
    }
  };

  const handleError = () => {
    onError("Error al usar la cámara o al escanear el código.");
  };

  const resetScan = () => {
    setScanResult(null);
    onScan(null);
    onError(null);
  };

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  useEffect(() => {
    if (!openScan) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      setHasCameraAccess(null);
      return;
    }

    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode } })
      .then((stream) => {
        streamRef.current = stream;
        setHasCameraAccess(true);
        onError(null);
      })
      .catch(() => {
        setHasCameraAccess(false);
        onError(
          "No se pudo acceder a la cámara. Verifica los permisos del navegador."
        );
      });

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [openScan, facingMode, onError]);

  return (
    <div className="flex flex-column align-items-center">
      <Button
        icon="pi pi-qrcode"
        tooltip={label}
        rounded
        onClick={() => setOpenScan(true)}
      ></Button>

      <Dialog
        header="QR scanner"
        visible={openScan}
        onHide={() => setOpenScan(false)}
      >
        <div className="flex flex-column align-items-center justify-content-center p-5 ">
          {hasCameraAccess === false && (
            <p className="text-center text-sm text-red-600">{error}</p>
          )}

          {hasCameraAccess && !scanResult && (
            <>
              {title && <h2>{title}</h2>}
              {description && <p>{description}</p>}
              <div className="w-full overflow-hidden">
                <Scanner
                  onScan={handleScan}
                  onError={handleError}
                  constraints={{
                    facingMode,
                    width: { ideal: 320 },
                    height: { ideal: 240 },
                  }}
                  formats={["qr_code", "code_128", "ean_13"]}
                />
                <div className="flex align-items-center justify-content-center pt-2">
                  <Button
                    onClick={toggleCamera}
                    label="Cambiar cámara"
                    icon="pi pi-sync"
                    size="small"
                    severity="secondary"
                    outlined
                    rounded
                  />
                </div>
              </div>
            </>
          )}

          {scanResult && !error && (
            <div className="flex align-items-center justify-content-center gap-3">
              <i className="pi pi-spin pi-spinner" />
              <p>Procesando información</p>
            </div>
          )}
          {scanResult && error && (
            <div className="flex flex-column gap-7">
              <div>
                <h1 className="mb-2">No hemos podido leer el código QR.</h1>
                <span>{error}</span>
              </div>

              <Button
                className="gap-3"
                onClick={resetScan}
                icon="pi pi-refresh"
                label="Reintentar"
              ></Button>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default ButtonScanQR;
